/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler} from 'react';
import {makeStyles} from '@material-ui/core';
import {IconButton} from '../../components/buttons/IconButton';
import {VolumeIcon} from '../../icons/VolumeIcon';
import {VolumeMuteIcon} from '../../icons/VolumeMuteIcon';
import {IconSize} from '../../types/types';
import Slider from '../slider/Slider';

export interface VolumeProps {
  volume: number;
  size?: IconSize | number;
  handleVolumeIcon: MouseEventHandler<HTMLButtonElement>;
  handleVolumeChange: Function;
  displayRange?: boolean;
}

const useStyles = makeStyles((theme) => ({
  volume : {
    display     : 'flex',
    alignItems  : 'center',
    width       : '120px',
    marginRight : '25px'
  },
  volumeIcon : {
    marginRight : '10px'
  }
}));

/**
 * @Volume
 *
 * Defines the Volume Controls Component. It is for controlling the volume of react video player
 *
 * Figma link to refer Progress bar component:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/02_DLS_Components?node-id=4060%3A43298
 *
 * ```typescript
 * import Volume from '@ion/dls/src/components/videoplayer/Volume';
 *
 *  function handleVolumeChange(e, value) {
 *   const volume=parseFloat(value);
 *   setVolume(volume);
 * }
 *
 * function handleVolumeIcon() {
 *   const currVolume = (volume === 0) ? prevVolume : 0;
 *   setVolume(currVolume);
 * }
 *
 * <Volume volume={volume}
 *    handleVolumeIcon={handleVolumeIcon}
 *    handleVolumeChange={handleVolumeChange} />
 *
 * ```
 */
const Volume = ({
  volume,
  size = 24,
  handleVolumeIcon,
  handleVolumeChange,
  displayRange = true
}: VolumeProps) => {
  const classes = useStyles({displayRange});
  const spacing = displayRange ? '12px' : '0px';

  return (
    <div className={!displayRange ? classes.volumeIcon : classes.volume}>
      <IconButton style={{marginRight: spacing}}
        onClick={handleVolumeIcon}
        icon={volume ? <VolumeIcon size={size} /> : <VolumeMuteIcon size={size} />}
        padding={!displayRange && '2px'} />
      {displayRange && (
        <Slider step={0.1}
          max={1}
          value={volume}
          onSlide={function(e, val) { handleVolumeChange(e, val); }} />
      )}
    </div>
  );
};

export default Volume;
